import React, { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import clsx from "clsx";
import { Category } from "@/dto/category";
import ChevronDownIcon from "@/components/_icons/chevronDownIcon";
import { useIntersectionObserver } from "@/utils/useIntersectionObserver";
import { SecondaryCarouselNavigationToggleButton as ToggleButton } from "./secondaryCarouselNavigationToggleButton";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";
import { clientLogger } from "@/utils/clientLogger";

interface Props {
  categories: Category[];
  isScrolledDown?: boolean;
}

export const SecondaryCarouselNavigationBar = ({
  categories,
  isScrolledDown,
}: Props) => {
  const scrollContainerRef = useRef<HTMLOListElement>(null);
  const firstCategoryRef = useRef<HTMLAnchorElement>(null);
  const lastCategoryRef = useRef<HTMLAnchorElement>(null);
  const leftButtonRef = useRef<HTMLButtonElement>(null);

  const [activeHash, setActiveHash] = useState("");
  const isScrollingLocked = useRef(true);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const searchParams = useSearchParams();
  const pathName = usePathname();
  const hasSearchQuery = searchParams?.get("query") !== null;
  const t = useTranslations("NavigationBar");

  const isFirstCategoryInView: boolean = useIntersectionObserver(
    firstCategoryRef,
    { threshold: 0.8 }
  );

  const isLastCategoryInView: boolean = useIntersectionObserver(
    lastCategoryRef,
    { threshold: 0.1 }
  );

  const getScrollOffset = () => {
    const TAILWIND_OFFSET = 40; // px-10
    return leftButtonRef.current?.offsetWidth || TAILWIND_OFFSET;
  };

  const scrollToCategory = (hash = activeHash) => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    const activeLink = Array.from(container.querySelectorAll("a")).find(
      (link) => link.getAttribute("href")?.endsWith(hash)
    ) as HTMLElement;

    if (activeLink?.parentElement) {
      const listItem = activeLink.parentElement;
      const offset = getScrollOffset();

      const targetScrollPos = listItem.offsetLeft - offset;

      container.scrollTo({
        left: Math.max(0, targetScrollPos),
        behavior: "smooth",
      });
    }
  };

  const scrollIntoPageView = (targetHash: string) => {
    const id = decodeURIComponent(targetHash.substring(1));
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const pollForPageCategories = () => {
    const checkElementsExist = () => {
      return categories.some((category) => {
        const elementById = document.getElementById(category.name);
        const elementByHyphen = document.getElementById(
          category.name.replaceAll(/\s+/g, "-")
        );

        return elementById || elementByHyphen;
      });
    };

    return new Promise<void>((resolve) => {
      if (checkElementsExist()) {
        resolve();
        return;
      }

      const intervalId = setInterval(() => {
        const isPageReady = checkElementsExist();
        if (isPageReady) {
          clearInterval(intervalId);
          resolve();
        }
      }, 1000);

      // Safety timeout: stop polling after a minute.
      setTimeout(() => {
        clearInterval(intervalId);
        resolve();
      }, 60000);
    });
  };

  // Observe page scrolling to update activeHash based on visible sections
  useEffect(() => {
    // No observation if too few categories, or if search is active
    if (categories.length <= 1 || hasSearchQuery) return;

    // "Ignore the top 40% and the bottom 55% of the screen".
    const observerOptions = {
      root: null,
      rootMargin: "-40% 0px -55% 0px",
      threshold: 0,
    };

    const findMatchedCategory = (activeId: string) => {
      return categories.find(
        (cat) =>
          cat.name === activeId || cat.name.replaceAll(/\s+/g, "-") === activeId
      );
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      // If the user just clicked a button, ignore scroll events for a moment
      if (isScrollingLocked.current) return;

      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          const activeId = entry.target.id;
          const matchedCategory = findMatchedCategory(activeId);

          if (matchedCategory) {
            setActiveHash(`#${encodeURIComponent(matchedCategory.name)}`);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    const attachObservers = () => {
      categories.forEach((category) => {
        const elementById = document.getElementById(category.name);

        const elementByHyphen = document.getElementById(
          category.name.replaceAll(/\s+/g, "-")
        );

        const target = elementById || elementByHyphen;

        if (target) {
          observer.observe(target);
        }
      });
    };

    const initObservers = async () => {
      await pollForPageCategories();
      attachObservers();
    };

    initObservers();

    return () => {
      observer.disconnect();
    };
  }, [categories, hasSearchQuery]);

  useEffect(() => {
    const handlePathChange = async () => {
      isScrollingLocked.current = true;
      const hash = getInitialHash();

      await pollForPageCategories();

      setActiveHash(hasSearchQuery ? "" : hash);
      scrollToCategory(hash);

      setTimeout(() => {
        isScrollingLocked.current = false;
      }, 1200);
    };

    handlePathChange();
  }, [pathName]);

  // Active link from URL history
  useEffect(() => {
    // For going back/forward in history
    const handleHashChange = () => {
      const hash = decodeURIComponent(globalThis.location.hash);

      setActiveHash(hash);
      scrollToCategory(hash);
    };

    globalThis.addEventListener("hashchange", handleHashChange);

    return () => globalThis.removeEventListener("hashchange", handleHashChange);
  }, []);

  const getInitialHash = () => {
    try {
      if (globalThis.location.hash) {
        const hash = decodeURIComponent(globalThis.location.hash);

        return hash;
      }

      if (categories.length) {
        const category = categories[0];
        const categoryLink = hasSearchQuery
          ? `${pathName}#${category.name}`
          : `#${category.name}`;

        return categoryLink;
      }

      return "";
    } catch {
      clientLogger.debug("Failed to decode URL hash.");

      if (categories.length) {
        const category = categories[0];
        const categoryLink = hasSearchQuery
          ? `${pathName}#${category.name}`
          : `#${category.name}`;

        return categoryLink;
      }

      return "";
    }
  };

  // Active hash on initial load
  useEffect(() => {
    const initialHash = getInitialHash();

    setActiveHash(hasSearchQuery ? "" : initialHash);
    scrollToCategory(initialHash);
  }, []);

  // Scroll handling for left/right buttons by one category
  const handleClickScrolling = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const items = Array.from(container.children) as HTMLElement[];
    const scrollLeft = Math.ceil(container.scrollLeft);
    const containerWidth = container.clientWidth;
    const buttonBuffer = 50;
    const tolerance = 2;

    if (direction === "right") {
      const currentVisibleRight = scrollLeft + containerWidth;
      // The first crossing item on right edge boundary
      const targetItem = items.find((item) => {
        const itemRight = item.offsetLeft + item.offsetWidth;
        return itemRight > currentVisibleRight + tolerance;
      });

      if (targetItem) {
        // Align right edge
        const itemRight = targetItem.offsetLeft + targetItem.offsetWidth;
        const newScrollLeft = itemRight - containerWidth + buttonBuffer;
        container.scrollTo({ left: newScrollLeft, behavior: "smooth" });
      }
    } else {
      // The first crossing item on left edge boundary
      const targetItem = items
        .slice()
        .reverse()
        .find((item) => {
          return item.offsetLeft < scrollLeft - tolerance;
        });

      if (targetItem) {
        // Align left edge
        container.scrollTo({
          left: targetItem.offsetLeft - buttonBuffer,
          behavior: "smooth",
        });
      }
    }
  };

  // When activeHash changes (due to page scrolling or clicking),
  // ensure the specific category tab is visible in the horizontal list.
  useEffect(() => {
    if (!activeHash || !scrollContainerRef.current || isScrollingLocked.current)
      return;

    scrollToCategory();
  }, [activeHash]);

  const handleLinkClick = (hash: string) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    const hashIndex = hash.indexOf("#");
    const targetHash = hashIndex !== -1 ? hash.substring(hashIndex) : "";

    isScrollingLocked.current = true;
    setActiveHash(targetHash);

    const SCROLL_LOCK_TIME = 2000;

    scrollIntoPageView(targetHash);
    scrollToCategory(targetHash);

    // Unlock after scroll prevents conflict
    clickTimeoutRef.current = setTimeout(() => {
      isScrollingLocked.current = false;
    }, SCROLL_LOCK_TIME);
  };

  const focusCategoryHeader = (categoryName: string) => {
    const headerId = `${categoryName.replaceAll(/\s+/g, "-")}-header`;
    const headerElement = document.getElementById(headerId);

    if (headerElement) {
      headerElement.focus({ preventScroll: true });
    }
  };

  const handleCategoryInteraction = (
    e: React.SyntheticEvent,
    categoryName: string,
    categoryLink: string
  ) => {
    if (e.nativeEvent instanceof KeyboardEvent) {
      if (e.nativeEvent.key !== " ") {
        return;
      }
      e.preventDefault();

      const targetHash = categoryLink.includes("#")
        ? categoryLink.substring(categoryLink.indexOf("#"))
        : categoryLink;

      globalThis.location.hash = targetHash;
    }

    handleLinkClick(categoryLink);

    setTimeout(() => {
      focusCategoryHeader(categoryName);
    }, 50);
  };

  // Wait for page DOM, then scroll unlock.
  useEffect(() => {
    const checkIfPageReady = async () => {
      await pollForPageCategories();

      if (globalThis.location.hash) {
        const hash = getInitialHash();
        scrollIntoPageView(hash);
      }

      setTimeout(() => {
        isScrollingLocked.current = false;
      }, 2500);
    };

    checkIfPageReady();
  }, []);

  return (
    <div
      className={clsx(
        "w-full overflow-x-hidden flex sm:px-10 sm:py-2 items-center",
        {
          "shadow-[0_4px_10px_0_rgba(0,0,0,0.1)]": isScrolledDown,
        }
      )}
      data-testid={applyDataTestId(
        pom.header.navigationHorizontalScrollContainer
      )}
    >
      {categories.length > 1 && (
        <ToggleButton
          categories={categories}
          handleLinkClick={handleLinkClick}
        />
      )}

      <div className="relative items-stretch self-stretch place-content-stretch flex flex-row w-full overflow-hidden">
        <button
          className={clsx("absolute top-0 bottom-0 bg-[#FBFBFB] px-2 z-[8]", {
            invisible: isFirstCategoryInView,
          })}
          ref={leftButtonRef}
          onClick={() => handleClickScrolling("left")}
          type="button"
          aria-label="scroll category navigation left"
        >
          <ChevronDownIcon className="rotate-90" />
        </button>

        <nav
          className="overflow-x-scroll no-scrollbar scroll-smooth"
          aria-label={t("categoryNavigationLabel")}
        >
          <ol
            ref={scrollContainerRef}
            className="flex w-full flex-nowrap overflow-x-scroll no-scrollbar scroll-smooth items-center gap-1"
          >
            {categories.map((category, index) => {
              const categoryLink = hasSearchQuery
                ? `${pathName}#${category.name}`
                : `#${encodeURIComponent(category.name)}`;

              const isActive = activeHash === categoryLink;

              let categoryRef;
              if (index === 0) {
                categoryRef = firstCategoryRef;
              } else if (index === categories.length - 1) {
                categoryRef = lastCategoryRef;
              }

              return (
                <li key={category.id}>
                  <Link
                    className={clsx(
                      "group whitespace-nowrap px-3 py-3 inline-block hover:bg-[#EDEDED] focus-visible:bg-[#EDEDED] focus-within:outline-none rounded-xl transition-[background] duration-300"
                    )}
                    href={categoryLink}
                    onClick={(e) =>
                      handleCategoryInteraction(e, category.name, categoryLink)
                    }
                    onKeyDown={(e) =>
                      handleCategoryInteraction(e, category.name, categoryLink)
                    }
                    ref={categoryRef}
                    data-testid={applyDataTestId(
                      pom.landingPage.category(category.name)
                    )}
                    id={
                      index === 0
                        ? pom.accessibilityIds.secondaryNavStart
                        : undefined
                    }
                  >
                    <div className="grid items-center">
                      <span className="font-bold opacity-0 invisible col-start-1 row-start-1 pointer-events-none">
                        {category.name}
                      </span>
                      <span
                        className={clsx(
                          "col-start-1 row-start-1 transition-all",
                          {
                            "font-bold": isActive,
                            "font-normal": !isActive,
                          }
                        )}
                      >
                        {category.name}
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ol>
        </nav>

        <button
          className={clsx(
            "absolute top-0 bottom-0 right-0 pr-1 pl-4 bg-[linear-gradient(90deg,rgba(255,255,255,0.00)_0%,#FBFBFB_28.57%)] z-10",
            {
              invisible: isLastCategoryInView,
            }
          )}
          onClick={() => handleClickScrolling("right")}
          type="button"
          aria-label="scroll category navigation right"
        >
          <ChevronDownIcon className="-rotate-90" />
        </button>
      </div>
    </div>
  );
};
